import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "@heroui/react";
import {
  api,
  ScaledRecipe,
  ScaledRecipeBase,
  type ScaledIngredient,
} from "../api/client";
import { formatUnit, MEAL_TYPE_LABELS } from "../constants";
import { PhotoPicker } from "../components/PhotoPicker";
import { RecipeModal } from "../components/RecipeModal";

export function RecipeView() {
  const { id } = useParams();
  const nav = useNavigate();
  const recipeId = Number(id);

  const [servings, setServings] = useState(0); // 0 = racions base (les del backend)
  const [recipe, setRecipe] = useState<ScaledRecipe | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [cooking, setCooking] = useState(false);
  const [cartAdded, setCartAdded] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setNotFound(false);
    api
      .get<ScaledRecipe>(`/recipes/${recipeId}?servings=${servings}`)
      .then((r) => {
        if (cancelled) return;
        setRecipe(r);
        setFavorite(r.favorite);
        if (servings === 0) setServings(r.effectiveServings);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [recipeId, servings]);

  async function addIngredientToCart(ing: ScaledIngredient) {
    try {
      await api.post("/shopping", {
        name: ing.name,
        quantity: ing.quantity,
        unit: ing.unit,
        ingredientId: ing.ingredientId,
      });
      setCartAdded((prev) => new Set([...prev, ing.id]));
      setTimeout(() => {
        setCartAdded((prev) => {
          const next = new Set(prev);
          next.delete(ing.id);
          return next;
        });
      }, 2000);
    } catch {}
  }

  async function changePhoto(url: string | null) {
    const prev = recipe?.imageUrl ?? null;
    setRecipe((r) => (r ? { ...r, imageUrl: url } : r)); // optimista
    try {
      await api.patch(`/recipes/${recipeId}/image`, { imageUrl: url });
      window.dispatchEvent(new Event("cookmenu:data-changed"));
    } catch {
      setRecipe((r) => (r ? { ...r, imageUrl: prev } : r));
    }
  }

  async function toggleFavorite() {
    const next = !favorite;
    setFavorite(next); // optimista
    try {
      if (next) await api.post(`/recipes/${recipeId}/favorite`);
      else await api.del(`/recipes/${recipeId}/favorite`);
      window.dispatchEvent(new Event("cookmenu:data-changed"));
    } catch {
      setFavorite(!next);
    }
  }

  if (notFound) {
    return <div className="empty">Recepta no trobada.</div>;
  }

  if (!recipe) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const effServings = recipe.effectiveServings;

  return (
    <div>
      <button className="btn btn-ghost" style={{ marginBottom: 16 }} onClick={() => nav("/receptes")}>
        <BackIcon /> Receptes
      </button>

      <div className="rd-grid">
        <div className="rd-hero card">
          <PhotoPicker
            type="recipe"
            name={recipe.name}
            imageUrl={recipe.imageUrl}
            meal={recipe.mealType}
            fill
            onChange={changePhoto}
          />
        </div>

        <div className="rd-head">
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="tag accent">{MEAL_TYPE_LABELS[recipe.mealType]}</span>
            {recipe.tags.map((t) => (
              <span key={t} className="tag">
                {t}
              </span>
            ))}
          </div>
          <h1>{recipe.name}</h1>
          <div style={{ display: "flex", gap: 18, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
            {recipe.prepTimeMin != null && (
              <span className="meta">
                <ClockIcon /> {recipe.prepTimeMin} min
              </span>
            )}
            <KcalMeta ingredients={recipe.ingredients} servings={effServings} />
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            {recipe.steps.length > 0 && (
              <button className="btn btn-primary btn-lg" onClick={() => setCooking(true)}>
                👨‍🍳 Comença a cuinar
              </button>
            )}
            <button className="btn btn-ghost btn-lg" onClick={() => nav(`/receptes/${recipeId}/edita`)}>
              <PencilIcon /> Edita
            </button>
            <button
              className="btn btn-ghost btn-lg"
              onClick={() => nav(`/receptes/nova?duplica=${recipeId}`)}
            >
              <CopyIcon /> Duplica
            </button>
            <button
              className="btn btn-ghost btn-lg btn-icon"
              onClick={toggleFavorite}
              aria-label={favorite ? "Treu de preferits" : "Marca preferida"}
              title={favorite ? "Treu de preferits" : "Marca preferida"}
            >
              <span className={`star ${favorite ? "on" : ""}`}>
                <StarIcon on={favorite} />
              </span>
            </button>
          </div>
          {recipe.notes && (
            <p className="text-muted" style={{ marginTop: 12, fontWeight: 500, lineHeight: 1.5 }}>
              {recipe.notes}
            </p>
          )}
        </div>
      </div>

      <div className="rd-cols">
        <section className="card rd-panel">
          <div className="rd-panel-head">
            <h3>Ingredients</h3>
            <div className="stepper">
              <button onClick={() => setServings(Math.max(1, effServings - 1))} aria-label="Una ració menys">
                −
              </button>
              <b>
                {effServings} {effServings === 1 ? "ració" : "racions"}
              </b>
              <button onClick={() => setServings(effServings + 1)} aria-label="Una ració més">
                +
              </button>
            </div>
          </div>
          <ul className="ing-ul">
            {recipe.ingredients.map((i) => (
              <li key={i.id}>
                <span className="q">
                  {formatQuantity(i.quantity)} {formatUnit(i.unit, i.quantity)}
                </span>
                <span style={{ flex: 1 }}>
                  {i.name}
                  {i.note ? <span className="note"> ({i.note})</span> : null}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  onClick={() => addIngredientToCart(i)}
                  aria-label={`Afegeix ${i.name} a la compra`}
                  title="Afegeix a la compra"
                  style={{ flexShrink: 0, color: cartAdded.has(i.id) ? "oklch(62% 0.19 145)" : undefined }}
                >
                  {cartAdded.has(i.id) ? <CheckIcon /> : <CartIcon />}
                </button>
              </li>
            ))}
          </ul>

          {recipe.subrecipes.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <h3 className="section-title" style={{ marginBottom: 8 }}>
                Subreceptes
              </h3>
              <div className="rd-sub">
                {recipe.subrecipes.map((sr) => (
                  <button
                    key={sr.id}
                    type="button"
                    className="rd-sub-row"
                    onClick={() => nav(`/receptes/${sr.id}`)}
                  >
                    <span style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{sr.name}</span>
                      <span className="text-muted" style={{ fontSize: 12.5, fontWeight: 600 }}>
                        {sr.ingredients.length} ingredients
                        {sr.steps.length > 0 ? ` · ${sr.steps.length} passos` : ""}
                      </span>
                    </span>
                    <span className="text-muted" style={{ fontSize: 12.5, fontWeight: 700 }}>
                      veure →
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {recipe.steps.length > 0 && (
          <section className="card rd-panel">
            <div className="rd-panel-head">
              <h3>Passos</h3>
            </div>
            <ol className="step-ol">
              {recipe.steps.map((s, i) => (
                <StepRow key={i} index={i + 1} text={s} />
              ))}
            </ol>
          </section>
        )}

        <NutritionPanel ingredients={recipe.ingredients} servings={effServings} />
      </div>

      {cooking && (
        <RecipeModal
          recipeId={recipeId}
          servings={effServings}
          startCooking
          onClose={() => setCooking(false)}
        />
      )}
    </div>
  );
}

function StepRow({ index, text }: { index: number; text: string }) {
  const colonIdx = text.indexOf(":");
  const hasTitle = colonIdx > 0 && colonIdx <= 60;
  const title = hasTitle ? text.slice(0, colonIdx) : null;
  const body = hasTitle ? text.slice(colonIdx + 1).trim() : text;
  return (
    <li>
      <span className="n">{index}</span>
      <p>
        {hasTitle && <strong style={{ fontWeight: 700 }}>{title}: </strong>}
        {body}
      </p>
    </li>
  );
}

/* ---------------- Nutrició: anelles "Per ració" ---------------- */

const UNIT_TO_GRAMS: Record<string, number> = {
  g: 1, kg: 1000, ml: 1, l: 1000, tsp: 5, tbsp: 15,
};

function toGrams(quantity: number, unit: string, gramsPerUnit?: number | null): number | null {
  const u = unit.toLowerCase();
  if (u === "u" || u === "unitat" || u === "unidat") {
    return gramsPerUnit ? quantity * gramsPerUnit : null;
  }
  const factor = UNIT_TO_GRAMS[u];
  return factor ? quantity * factor : null;
}

interface Macros {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

function useMacros(ingredients: ScaledIngredient[], servings: number): Macros | null {
  return useMemo(() => {
    const acc: Macros = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    let hasData = false;
    for (const ing of ingredients) {
      if (!ing.nutrition) continue;
      const grams = toGrams(ing.quantity, ing.unit, ing.gramsPerUnit);
      if (grams === null) continue;
      const f = grams / 100;
      if (ing.nutrition.calories != null) {
        acc.calories += ing.nutrition.calories * f;
        hasData = true;
      }
      if (ing.nutrition.protein != null) acc.protein += ing.nutrition.protein * f;
      if (ing.nutrition.carbs != null) acc.carbs += ing.nutrition.carbs * f;
      if (ing.nutrition.fat != null) acc.fat += ing.nutrition.fat * f;
    }
    if (!hasData || servings <= 0) return null;
    return {
      calories: acc.calories / servings,
      protein: acc.protein / servings,
      carbs: acc.carbs / servings,
      fat: acc.fat / servings,
    };
  }, [ingredients, servings]);
}

function KcalMeta({ ingredients, servings }: { ingredients: ScaledIngredient[]; servings: number }) {
  const macros = useMacros(ingredients, servings);
  if (!macros) return null;
  return (
    <span className="meta">
      <FlameIcon /> {Math.round(macros.calories)} kcal/ració
    </span>
  );
}

function NutritionPanel({ ingredients, servings }: { ingredients: ScaledIngredient[]; servings: number }) {
  const macros = useMacros(ingredients, servings);
  if (!macros) return null;
  return (
    <section className="card rd-panel rd-nutri">
      <div className="rd-panel-head">
        <h3>Per ració</h3>
      </div>
      <div className="rings">
        <Ring label="kcal" value={Math.round(macros.calories)} max={700} color="oklch(68% 0.17 47)" />
        <Ring label="proteïna" value={Math.round(macros.protein)} unit="g" max={40} color="oklch(58% 0.13 300)" />
        <Ring label="carbohidrats" value={Math.round(macros.carbs)} unit="g" max={60} color="oklch(70% 0.13 80)" />
        <Ring label="greix" value={Math.round(macros.fat)} unit="g" max={40} color="oklch(66% 0.14 55)" />
      </div>
    </section>
  );
}

function Ring({
  label,
  value,
  unit,
  max,
  color,
}: {
  label: string;
  value: number;
  unit?: string;
  max: number;
  color: string;
}) {
  const pct = Math.min(100, (value / max) * 100);
  const C = 2 * Math.PI * 26;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <svg width="68" height="68" viewBox="0 0 68 68">
        <circle cx="34" cy="34" r="26" fill="none" stroke="var(--surface-tertiary)" strokeWidth="7" />
        <circle
          cx="34"
          cy="34"
          r="26"
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={C * (1 - pct / 100)}
          transform="rotate(-90 34 34)"
        />
        <text x="34" y="34" textAnchor="middle" dominantBaseline="central" fontWeight="800" fontSize="15" fill="var(--foreground)">
          {value}
          {unit}
        </text>
      </svg>
      <small style={{ fontWeight: 700, fontSize: 12, color: "var(--muted)" }}>{label}</small>
    </div>
  );
}

function formatQuantity(q: number): string {
  if (Number.isInteger(q)) return String(q);
  return (Math.round(q * 100) / 100).toString();
}

/* ---------------- Icones ---------------- */

function BackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 16 14" />
    </svg>
  );
}
function FlameIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2c1 3 4 4.5 4 8a4 4 0 0 1-8 0c0-1.5.5-2.5 1-3.5C8 8 7 10 7 13a5 5 0 0 0 10 0c0-4-3-7-5-11z" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function StarIcon({ on }: { on: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill={on ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
      <polygon points="12 2.5 15 9 22 9.7 16.8 14.3 18.4 21 12 17.4 5.6 21 7.2 14.3 2 9.7 9 9" />
    </svg>
  );
}
function CartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
